import React, { createContext, useContext, useEffect, useState } from 'react';

// Business interface for multi-tenant system
interface Business {
  id: string;
  email: string;
  businessName: string;
  businessType: string;
  country: string;
  subscriptionStatus: string;
  currency?: string; // Business currency from settings
}

// Custom auth context for multi-tenant system
interface AuthContextType {
  business: Business | null;
  token: string | null;
  loading: boolean;
  signUp: (signupData: SignupData) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
}

// Signup data interface
interface SignupData {
  email: string;
  password: string;
  country: string;
  businessName: string;
  businessType: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [business, setBusiness] = useState<Business | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated
  const isAuthenticated = business !== null && token !== null;

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = async () => {
      setLoading(true);
      try {
        const storedToken = localStorage.getItem('smartdry_token');
        const storedBusiness = localStorage.getItem('smartdry_business');
        
        console.log('🔍 Checking stored auth:', { 
          hasToken: !!storedToken, 
          hasBusiness: !!storedBusiness 
        });
        
        if (storedToken && storedBusiness) {
          try {
            const businessData = JSON.parse(storedBusiness);
            console.log('🏢 Restoring business from storage:', businessData.businessName);
            
            // Set auth state immediately from storage
            setToken(storedToken);
            setBusiness(businessData);
            
            console.log('✅ Session restored successfully');
          } catch (parseError) {
            console.error('❌ Failed to parse stored business:', parseError);
            localStorage.removeItem('smartdry_token');
            localStorage.removeItem('smartdry_business');
          }
        } else {
          console.log('ℹ️ No complete auth data found - user needs to login');
        }
      } catch (error) {
        console.error('❌ Session check error:', error);
        localStorage.removeItem('smartdry_token');
        localStorage.removeItem('smartdry_business');
      } finally {
        setLoading(false);
      }
    };

    checkExistingSession();
  }, []); // Only run once on mount

  const signUp = async (signupData: SignupData) => {
    try {
      console.log('🔐 Attempting signup for:', signupData.email);
      
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(signupData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store token and business data
        setToken(data.data.token);
        setBusiness(data.data.business);
        localStorage.setItem('smartdry_token', data.data.token);
        localStorage.setItem('smartdry_business', JSON.stringify(data.data.business));
        
        console.log('✅ Signup successful for business:', data.data.business.businessName);
        return { data, error: null };
      } else {
        console.error('❌ Signup failed:', data.message);
        return { data: null, error: { message: data.message || 'Signup failed' } };
      }
    } catch (error) {
      console.error('❌ Signup error:', error);
      return { 
        data: null, 
        error: { message: 'Network error. Please check your connection and try again.' } 
      };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting login for:', email);
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store token and business data
        setToken(data.data.token);
        setBusiness(data.data.business);
        localStorage.setItem('smartdry_token', data.data.token);
        localStorage.setItem('smartdry_business', JSON.stringify(data.data.business));
        
        console.log('✅ Login successful for business:', data.data.business.businessName);
        return { data, error: null };
      } else {
        console.error('❌ Login failed:', data.message);
        return { data: null, error: { message: data.message || 'Login failed' } };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return { 
        data: null, 
        error: { message: 'Network error. Please check your connection and try again.' } 
      };
    }
  };

  const signOut = async () => {
    try {
      console.log('🔓 Logging out...');
      
      // Call logout endpoint to invalidate session
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
      
      // Clear local state and storage
      setToken(null);
      setBusiness(null);
      localStorage.removeItem('smartdry_token');
      localStorage.removeItem('smartdry_business');
      
      console.log('✅ Logged out successfully');
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Still clear local state even if API call fails
      setToken(null);
      setBusiness(null);
      localStorage.removeItem('smartdry_token');
      localStorage.removeItem('smartdry_business');
    }
  };

  const value = {
    business,
    token,
    loading,
    signUp,
    signIn,
    signOut,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};